import { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import BackArrow from "@/shared/assets/icons/back-arrow.png";
import { Preloader } from "@/components";
import { useGetQuizQuery, useAddAnswerMutation, useFindAnswersQuery, AnswerType, QuizQuestion } from "@/store/quizApi";
import { tg } from "@/constants/telegram";
import { useAppSelector } from "@/shared/hooks";

// Правила для условного отображения вопросов с номером > 100
type QuestionRule = {
  questionId: string; // ID вопроса, который нужно показать (например, "107")
  dependsOn: string; // ID вопроса, от которого зависит показ (например, "7")
  condition: (answer: string | string[]) => boolean; // Функция проверки условия
};

// Конфигурация правил
const QUESTION_RULES: QuestionRule[] = [
  {
    questionId: "107",
    dependsOn: "7",
    condition: (answer) => {
      if (Array.isArray(answer)) {
        return answer.includes("1");
      }
      return answer === "1";
    },
  },
];

type QuizPageProps = {
  initData?: string;
};

export const QuizPage = ({ initData }: QuizPageProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const questionId = searchParams.get("question") || "1";
  const navigate = useNavigate();

  const { data: quizData, isLoading: isQuizLoading } = useGetQuizQuery();
  const [addAnswer, { isLoading: isSubmitting }] = useAddAnswerMutation();

  // Получаем initData из Telegram или пропсов
  const currentInitData = initData || tg?.initData || null;

  // Загружаем предыдущие ответы
  const { data: previousAnswersData, isLoading: isPreviousAnswersLoading } = useFindAnswersQuery(
    {
      data_type: ["full"],
    },
    { skip: !currentInitData }
  );

  const [selectedAnswers, setSelectedAnswers] = useState<Record<string, string[]>>({});
  const [textAnswers, setTextAnswers] = useState<Record<string, string>>({});
  const [otherAnswers, setOtherAnswers] = useState<Record<string, string>>({}); // Текст для "Другое"
  const [bodyParams, setBodyParams] = useState<Record<string, { age: string; weight: string; height: string }>>({}); // Возраст, вес, рост
  const [answersLoaded, setAnswersLoaded] = useState(false);
  const progressRef = useRef<HTMLDivElement>(null);

  // Если параметра нет → редиректим на question=1
  useEffect(() => {
    const questionParam = searchParams.get("question");
    if (!questionParam) {
      setSearchParams({ question: "1" }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionId]);

  // Загружаем предыдущие ответы при загрузке вопросов
  useEffect(() => {
    if (quizData && previousAnswersData?.answers && !answersLoaded) {
      const answers = previousAnswersData.answers;
      const newSelectedAnswers: Record<string, string[]> = {};
      const newTextAnswers: Record<string, string> = {};
      const newOtherAnswers: Record<string, string> = {};
      const newBodyParams: Record<string, { age: string; weight: string; height: string }> = {};

      // Проходим по всем вопросам
      Object.keys(quizData).forEach((questionId) => {
        const question = quizData[questionId];
        const savedAnswer = answers[questionId];

        if (savedAnswer !== undefined && savedAnswer !== null) {
          const answerStr = String(savedAnswer);
          
          // Если вопрос имеет варианты ответов, пытаемся определить тип ответа
          if (question.answers) {
            // Сначала проверяем, является ли ответ текстом (возможно, с "Другое")
            const isNumericAnswer = /^\d+$/.test(answerStr) || (answerStr.includes("%") && !answerStr.includes(" "));
            
            if (!isNumericAnswer && answerStr.length > 0) {
              // Это текстовый ответ - пытаемся разобрать
              const foundAnswerIds: string[] = [];
              let otherText = answerStr;

              // Ищем тексты вариантов ответов в сохраненном ответе
              Object.entries(question.answers).forEach(([answerId, answerTextOption]) => {
                // Проверяем вхождение полного текста или текста без эмодзи
                const cleanAnswerText = answerTextOption.replace(/^[^\w\s]+/, "").trim();
                if (answerStr.includes(answerTextOption) || answerStr.includes(cleanAnswerText)) {
                  foundAnswerIds.push(answerId);
                  // Удаляем найденный текст из otherText
                  otherText = otherText
                    .replace(answerTextOption, "")
                    .replace(cleanAnswerText, "")
                    .replace(/^,\s*|,\s*$/g, "")
                    .trim();
                }
              });

              // Если нашли варианты ответов - значит был выбран "Другое"
              if (foundAnswerIds.length > 0) {
                newSelectedAnswers[questionId] = foundAnswerIds;
                if (otherText) {
                  newOtherAnswers[questionId] = otherText;
                }
              } else {
                // Просто текстовый ответ без вариантов (для вопросов с answer_type="text")
                if (question.answer_type === "text") {
                  newTextAnswers[questionId] = answerStr;
                }
              }
            } else {
              // Это числовой ответ или ответ с "%"
              if (question.answer_type === "one_button") {
                // Для one_button - номер ответа
                newSelectedAnswers[questionId] = [answerStr];
              } else if (question.answer_type === "many_buttons" || question.answer_type === "many_button") {
                // Для many_buttons - номера через "%"
                if (answerStr.includes("%")) {
                  newSelectedAnswers[questionId] = answerStr.split("%");
                } else {
                  newSelectedAnswers[questionId] = [answerStr];
                }
              }
            }
          } else {
            // Вопрос без вариантов ответов - просто текстовый ответ
            if (question.answer_type === "text") {
              // Проверяем, является ли это вопросом про возраст, вес и рост
              const isBodyParamsQ = question.text.toLowerCase().includes("возраст") && 
                                    question.text.toLowerCase().includes("вес") && 
                                    question.text.toLowerCase().includes("рост");
              
              if (isBodyParamsQ) {
                // Разбираем ответ на возраст, вес и рост (формат: "возраст, вес, рост")
                const parts = answerStr.split(",").map(p => p.trim());
                if (parts.length >= 3) {
                  newBodyParams[questionId] = {
                    age: parts[0] || "",
                    weight: parts[1] || "",
                    height: parts[2] || "",
                  };
                } else {
                  // Если формат неправильный, пытаемся разобрать по-другому
                  newBodyParams[questionId] = {
                    age: parts[0] || "",
                    weight: parts[1] || "",
                    height: parts[2] || "",
                  };
                }
              } else {
                newTextAnswers[questionId] = answerStr;
              }
            }
          }
        }
      });

      setSelectedAnswers(newSelectedAnswers);
      setTextAnswers(newTextAnswers);
      setOtherAnswers(newOtherAnswers);
      setBodyParams(newBodyParams);
      setAnswersLoaded(true);
    }
  }, [quizData, previousAnswersData, answersLoaded]);

  // Функция для проверки, должен ли вопрос быть показан
  const shouldShowQuestion = (qId: string, allAnswers: Record<string, string[]>, allTextAnswers: Record<string, string>): boolean => {
    const questionNum = Number(qId);
    
    // Вопросы с номером <= 100 показываются всегда
    if (questionNum <= 100) {
      return true;
    }

    // Для вопросов > 100 проверяем правила
    const rule = QUESTION_RULES.find((r) => r.questionId === qId);
    if (!rule) {
      // Если правила нет, не показываем вопрос
      return false;
    }

    // Получаем ответ на вопрос, от которого зависит показ
    const dependsOnAnswer = allAnswers[rule.dependsOn] || 
                             (allTextAnswers[rule.dependsOn] ? [allTextAnswers[rule.dependsOn]] : []);

    // Если ответ еще не дан, не показываем вопрос
    if (dependsOnAnswer.length === 0) {
      return false;
    }

    // Проверяем условие
    const answerToCheck = dependsOnAnswer.length === 1 ? dependsOnAnswer[0] : dependsOnAnswer;
    return rule.condition(answerToCheck);
  };

  // Получаем список всех вопросов, отсортированных по ID, с учетом правил
  const questionIds = useMemo(() => {
    if (!quizData) return [];
    
    const allQuestionIds = Object.keys(quizData).sort((a, b) => Number(a) - Number(b));
    const filteredIds: string[] = [];
    
    // Проходим по всем вопросам и добавляем их в правильном порядке
    allQuestionIds.forEach((qId) => {
      const questionNum = Number(qId);
      
      // Вопросы <= 100 добавляем всегда
      if (questionNum <= 100) {
        filteredIds.push(qId);
      } else {
        // Для вопросов > 100 проверяем правила
        if (shouldShowQuestion(qId, selectedAnswers, textAnswers)) {
          // Находим вопрос, от которого зависит показ, и вставляем после него
          const rule = QUESTION_RULES.find((r) => r.questionId === qId);
          if (rule) {
            const dependsOnIndex = filteredIds.indexOf(rule.dependsOn);
            if (dependsOnIndex >= 0) {
              // Вставляем после вопроса, от которого зависит
              filteredIds.splice(dependsOnIndex + 1, 0, qId);
            } else {
              // Если зависимый вопрос еще не в списке, добавляем в конец
              filteredIds.push(qId);
            }
          }
        }
      }
    });
    
    return filteredIds;
  }, [quizData, selectedAnswers, textAnswers]);

  const currentQuestionIndex = questionIds.indexOf(questionId);
  const currentQuestion: QuizQuestion | undefined = quizData?.[questionId];

  // Автоматическая прокрутка к текущему этапу
  useEffect(() => {
    if (progressRef.current && currentQuestionIndex >= 0) {
      const progressContainer = progressRef.current;
      const currentStepElement = progressContainer.children[currentQuestionIndex] as HTMLElement;
      if (currentStepElement) {
        const containerWidth = progressContainer.offsetWidth;
        const stepLeft = currentStepElement.offsetLeft;
        const stepWidth = currentStepElement.offsetWidth;
        const scrollLeft = stepLeft - containerWidth / 2 + stepWidth / 2;
        
        progressContainer.scrollTo({
          left: Math.max(0, scrollLeft),
          behavior: 'smooth'
        });
      }
    }
  }, [currentQuestionIndex]);

  if (isQuizLoading || !quizData || (currentInitData && isPreviousAnswersLoading && !answersLoaded)) {
    return <Preloader />;
  }

  if (!currentQuestion) {
    return (
      <div className="flex flex-col gap-3 pt-10 w-[90%] mx-auto max-w-[600px]">
        <p>Вопрос не найден</p>
      </div>
    );
  }

  const isOneButton = currentQuestion.answer_type === "one_button";
  const isManyButtons = currentQuestion.answer_type === "many_buttons" || currentQuestion.answer_type === "many_button";
  const isText = currentQuestion.answer_type === "text";
  
  // Проверяем, является ли это вопросом про возраст, вес и рост
  const isBodyParamsQuestion = isText && (
    currentQuestion.text.toLowerCase().includes("возраст") && 
    currentQuestion.text.toLowerCase().includes("вес") && 
    currentQuestion.text.toLowerCase().includes("рост")
  );

  const currentSelected = selectedAnswers[questionId] || [];
  const currentTextAnswer = textAnswers[questionId] || "";
  const currentOtherAnswer = otherAnswers[questionId] || "";
  const currentBodyParams = bodyParams[questionId] || { age: "", weight: "", height: "" };

  // Проверяем, выбран ли ответ "Другое"
  const hasOtherSelected = currentQuestion.answers
    ? currentSelected.some((answerId) => {
        const answerText = currentQuestion.answers![answerId];
        return answerText && (answerText.includes("Другое") || answerText.includes("✏️ Другое"));
      })
    : false;

  const handleAnswerSelect = (answerId: string) => {
    if (isOneButton) {
      // Для one_button - только один ответ
      setSelectedAnswers({ ...selectedAnswers, [questionId]: [answerId] });
      
      // Если сняли выбор "Другое", очищаем текст
      if (currentQuestion.answers) {
        const answerText = currentQuestion.answers[answerId];
        if (!answerText || (!answerText.includes("Другое") && !answerText.includes("✏️ Другое"))) {
          setOtherAnswers({ ...otherAnswers, [questionId]: "" });
        }
      }
    } else if (isManyButtons) {
      // Для many_buttons - несколько ответов
      const current = selectedAnswers[questionId] || [];
      const isDeselecting = current.includes(answerId);
      
      if (isDeselecting) {
        // Снимаем выбор
        const newSelected = current.filter((id) => id !== answerId);
        setSelectedAnswers({
          ...selectedAnswers,
          [questionId]: newSelected,
        });
        
        // Если сняли выбор "Другое", очищаем текст
        if (currentQuestion.answers) {
          const answerText = currentQuestion.answers[answerId];
          if (answerText && (answerText.includes("Другое") || answerText.includes("✏️ Другое"))) {
            setOtherAnswers({ ...otherAnswers, [questionId]: "" });
          }
        }
      } else {
        // Добавляем выбор
        setSelectedAnswers({
          ...selectedAnswers,
          [questionId]: [...current, answerId],
        });
      }
    }
  };

  const handleTextChange = (value: string) => {
    setTextAnswers({ ...textAnswers, [questionId]: value });
  };

  const handleOtherTextChange = (value: string) => {
    setOtherAnswers({ ...otherAnswers, [questionId]: value });
  };

  const handleBodyParamChange = (field: "age" | "weight" | "height", value: string) => {
    setBodyParams({
      ...bodyParams,
      [questionId]: {
        ...currentBodyParams,
        [field]: value,
      },
    });
  };

  const handleNext = async () => {
    // Проверка на заполненность
    if (isBodyParamsQuestion) {
      if (!currentBodyParams.age.trim() || !currentBodyParams.weight.trim() || !currentBodyParams.height.trim()) {
        toast.error("Пожалуйста, заполните все поля (возраст, вес, рост)");
        return;
      }
    } else if (isText && !currentTextAnswer.trim()) {
      toast.error("Пожалуйста, введите ответ");
      return;
    }

    if ((isOneButton || isManyButtons) && currentSelected.length === 0) {
      toast.error("Пожалуйста, выберите ответ");
      return;
    }

    // Если выбран "Другое", проверяем, что текст заполнен
    if (hasOtherSelected && !currentOtherAnswer.trim()) {
      toast.error("Пожалуйста, укажите, что вы хотели бы добавить");
      return;
    }

    try {
      let answerValue: string;
      let answerType: AnswerType;

      // Если это вопрос про возраст, вес и рост
      if (isBodyParamsQuestion) {
        answerValue = `${currentBodyParams.age}, ${currentBodyParams.weight}, ${currentBodyParams.height}`;
        answerType = AnswerType.TEXT;
      } else if (hasOtherSelected && (isOneButton || isManyButtons)) {
        // Если выбран "Другое", объединяем все ответы
        // Получаем тексты всех выбранных ответов кроме "Другое"
        const selectedAnswersTexts: string[] = [];
        const otherAnswerIds: string[] = [];

        if (currentQuestion.answers) {
          currentSelected.forEach((answerId) => {
            const answerText = currentQuestion.answers![answerId];
            if (answerText && (answerText.includes("Другое") || answerText.includes("✏️ Другое"))) {
              otherAnswerIds.push(answerId);
            } else {
              selectedAnswersTexts.push(answerText);
            }
          });
        }

        // Объединяем все ответы: выбранные (кроме "Другое") + текст из "Другое"
        const allAnswers = [
          ...selectedAnswersTexts,
          currentOtherAnswer.trim()
        ].filter(Boolean);

        answerValue = allAnswers.join(", ");
        answerType = AnswerType.TEXT;
      } else {
        // Обычная логика отправки
        answerValue = isText
          ? currentTextAnswer
          : isOneButton
          ? currentSelected[0]
          : currentSelected.join("%");

        answerType = isText
          ? AnswerType.TEXT
          : isOneButton
          ? AnswerType.ONE_BUTTON
          : AnswerType.MANY_BUTTONS;
      }

      await addAnswer({
        question_id: Number(questionId),
        answer: answerValue,
        answer_type: answerType,
      }).unwrap();

      // Обновляем состояние ответов для правильного пересчета списка вопросов
      if (isBodyParamsQuestion) {
        setBodyParams({ ...bodyParams, [questionId]: currentBodyParams });
        setTextAnswers({ ...textAnswers, [questionId]: answerValue });
      } else if (isText) {
        setTextAnswers({ ...textAnswers, [questionId]: answerValue });
      } else if (isOneButton) {
        setSelectedAnswers({ ...selectedAnswers, [questionId]: [String(answerValue)] });
      } else if (isManyButtons) {
        setSelectedAnswers({ ...selectedAnswers, [questionId]: currentSelected });
      }

      // Пересчитываем список вопросов с учетом нового ответа
      const updatedAnswers = isBodyParamsQuestion || isText
        ? { ...textAnswers, [questionId]: answerValue }
        : textAnswers;
      const updatedSelected = isOneButton || isManyButtons
        ? { ...selectedAnswers, [questionId]: isOneButton ? [String(answerValue)] : currentSelected }
        : selectedAnswers;

      // Получаем обновленный список вопросов
      const allQuestionIds = Object.keys(quizData).sort((a, b) => Number(a) - Number(b));
      const updatedQuestionIds: string[] = [];
      
      allQuestionIds.forEach((qId) => {
        const questionNum = Number(qId);
        if (questionNum <= 100) {
          updatedQuestionIds.push(qId);
        } else {
          if (shouldShowQuestion(qId, updatedSelected, updatedAnswers)) {
            const rule = QUESTION_RULES.find((r) => r.questionId === qId);
            if (rule) {
              const dependsOnIndex = updatedQuestionIds.indexOf(rule.dependsOn);
              if (dependsOnIndex >= 0) {
                updatedQuestionIds.splice(dependsOnIndex + 1, 0, qId);
              } else {
                updatedQuestionIds.push(qId);
              }
            }
          }
        }
      });

      // Переходим к следующему вопросу или завершаем
      const currentIndexInUpdated = updatedQuestionIds.indexOf(questionId);
      const nextIndex = currentIndexInUpdated + 1;
      if (nextIndex < updatedQuestionIds.length) {
        setSearchParams({ question: updatedQuestionIds[nextIndex] });
      } else {
        toast.success("Анкета завершена!");
        navigate("/equipments");
      }
    } catch (err) {
      console.error("Ошибка отправки", err);
      toast.error("Ошибка отправки ответа");
    }
  };

  const handleBack = () => {
    if (currentQuestionIndex > 0) {
      setSearchParams({ question: questionIds[currentQuestionIndex - 1] });
    } else {
      navigate(-1); // или на главную страницу
    }
  };

  // Парсим HTML текст вопроса - используем dangerouslySetInnerHTML для правильного отображения
  const getQuestionHTML = (htmlText: string) => {
    return { __html: htmlText };
  };

  // Вычисляем прогресс
  const progress = ((currentQuestionIndex + 1) / questionIds.length) * 100;

  return (
    <div className="flex flex-col gap-3 pt-10 pb-24 w-[90%] mx-auto max-w-[600px]">
      <header className="flex items-center gap-3 justify-between mb-6">
        <img
          src={BackArrow}
          className="w-[20px] cursor-pointer flex-shrink-0"
          alt="backArrow"
          onClick={handleBack}
        />

        <div 
          ref={progressRef}
          className="flex gap-1 overflow-x-auto flex-1 min-w-0 px-2 scrollbar-hide" 
          style={{ 
            scrollbarWidth: 'none', 
            msOverflowStyle: 'none',
            WebkitOverflowScrolling: 'touch'
          }}
        >
          <style>{`
            .scrollbar-hide::-webkit-scrollbar {
              display: none;
            }
          `}</style>
          {questionIds.map((_, index) => (
            <span
              key={index}
              className={`flex-shrink-0 w-8 h-[3px] rounded-full transition-colors duration-300 ${
                index < currentQuestionIndex + 1
                  ? "bg-[#4E7EFB]"
                  : "bg-[#c0c0c0]"
              }`}
            ></span>
          ))}
        </div>

        <div className="w-[20px] flex-shrink-0"></div>
      </header>

      <main className="flex flex-col gap-6">
        {/* Текст вопроса */}
        <div 
          className="text-[18px] leading-[28px]"
          dangerouslySetInnerHTML={getQuestionHTML(currentQuestion.text)}
        />

        {/* Варианты ответов */}
        {isOneButton || isManyButtons ? (
          <div className="flex flex-col gap-3">
            {currentQuestion.answers &&
              Object.entries(currentQuestion.answers).map(([answerId, answerText]) => {
                const isSelected = currentSelected.includes(answerId);
                return (
                  <button
                    key={answerId}
                    onClick={() => handleAnswerSelect(answerId)}
                    className={`
                      w-full p-4 rounded-[16px] text-left text-[18px]
                      transition-all duration-200
                      ${
                        isSelected
                          ? "bg-[#4E7EFB] text-white shadow-lg"
                          : "bg-white text-gray-800 shadow-md hover:shadow-lg"
                      }
                      active:scale-95
                    `}
                  >
                    {answerText}
                  </button>
                );
              })}
            
            {/* Поле для ввода "Другое", если выбрано */}
            {hasOtherSelected && (
              <div className="mt-2">
                <textarea
                  value={currentOtherAnswer}
                  onChange={(e) => handleOtherTextChange(e.target.value)}
                  placeholder="Что вы хотели бы добавить?"
                  className="
                    w-full p-4 rounded-[16px] text-[16px] min-h-[100px]
                    border-2 border-[#4E7EFB] focus:border-[#4E7EFB] focus:outline-none
                    resize-y
                  "
                />
              </div>
            )}
          </div>
        ) : isBodyParamsQuestion ? (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <label className="text-[16px] font-medium">Возраст</label>
              <input
                type="number"
                value={currentBodyParams.age}
                onChange={(e) => handleBodyParamChange("age", e.target.value)}
                placeholder="Введите возраст"
                className="
                  w-full p-4 rounded-[16px] text-[16px]
                  border-2 border-gray-200 focus:border-[#4E7EFB] focus:outline-none
                "
              />
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-[16px] font-medium">Вес (кг)</label>
              <input
                type="number"
                value={currentBodyParams.weight}
                onChange={(e) => handleBodyParamChange("weight", e.target.value)}
                placeholder="Введите вес"
                className="
                  w-full p-4 rounded-[16px] text-[16px]
                  border-2 border-gray-200 focus:border-[#4E7EFB] focus:outline-none
                "
              />
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-[16px] font-medium">Рост (см)</label>
              <input
                type="number"
                value={currentBodyParams.height}
                onChange={(e) => handleBodyParamChange("height", e.target.value)}
                placeholder="Введите рост"
                className="
                  w-full p-4 rounded-[16px] text-[16px]
                  border-2 border-gray-200 focus:border-[#4E7EFB] focus:outline-none
                "
              />
            </div>
          </div>
        ) : isText ? (
          <textarea
            value={currentTextAnswer}
            onChange={(e) => handleTextChange(e.target.value)}
            placeholder="Введите ваш ответ..."
            className="
              w-full p-4 rounded-[16px] text-[16px] min-h-[120px]
              border-2 border-gray-200 focus:border-[#4E7EFB] focus:outline-none
              resize-y
            "
          />
        ) : null}
      </main>

      {/* Кнопка продолжить - зафиксирована внизу экрана */}
      <div 
        className="
          fixed bottom-0 left-0 right-0 
          flex justify-center items-center 
          py-4 px-4 
          bg-white border-t border-gray-200
          shadow-lg
          z-50
        "
        style={{
          paddingBottom: "max(16px, env(safe-area-inset-bottom))"
        }}
      >
        <button
          onClick={handleNext}
          disabled={isSubmitting}
          className="
            bg-gradient-to-l from-gray-800 to-gray-500 
            text-white py-3 rounded-full text-center px-16 text-[22px]
            shadow-md hover:shadow-lg 
            active:scale-95 active:brightness-90
            transition-all duration-200
            disabled:opacity-50 disabled:cursor-not-allowed
            w-full max-w-[600px]
          "
        >
          {isSubmitting ? "Отправка..." : currentQuestionIndex < questionIds.length - 1 ? "Продолжить" : "Завершить"}
        </button>
      </div>
    </div>
  );
};


